<template>
  <div class="calendar-container">
    <div class="calendar-toolbar mb-3 d-flex justify-content-between align-items-center flex-wrap">
      <h5 class="mb-0">
        <i class="fas fa-calendar-alt mr-2"></i>
        {{ monthLabel }}
      </h5>
      <div>
        <base-button size="sm" type="neutral" @click="prevMonth">
          <i class="fas fa-chevron-left"></i>
        </base-button>
        <base-button size="sm" type="neutral" class="ml-1" @click="nextMonth">
          <i class="fas fa-chevron-right"></i>
        </base-button>
      </div>
    </div>

    <p class="text-muted small mb-3">
      Haz clic en un día para ver sus partes abajo. Los días con muchos partes muestran solo el total.
    </p>

    <div class="calendar-grid">
      <div class="calendar-weekdays">
        <div v-for="day in weekdays" :key="day" class="calendar-weekday">{{ day }}</div>
      </div>
      <div class="calendar-days">
        <div
          v-for="(day, index) in calendarDays"
          :key="day.date || `empty-${index}`"
          class="calendar-day"
          :class="{
            'empty-day': day.isEmpty,
            today: day.isToday,
            'has-events': day.events.length > 0,
            selected: selectedDate === day.date,
          }"
          @click="!day.isEmpty && selectDay(day)"
        >
          <template v-if="!day.isEmpty">
            <div class="day-header">
              <span class="day-number">{{ day.day }}</span>
              <span v-if="day.events.length" class="day-count">{{ day.events.length }}</span>
            </div>
            <div v-if="day.events.length" class="day-preview">
              <div class="preview-line">{{ day.events[0].numParte }} · {{ day.events[0].clienteCorto }}</div>
              <div v-if="day.events.length > 1" class="preview-more">
                +{{ day.events.length - 1 }} más
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div v-if="selectedDate" class="day-detail-panel mt-4">
      <div class="day-detail-header d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">
          Partes del {{ selectedDateLabel }}
          <span class="badge badge-primary ml-2">{{ selectedDayItems.length }}</span>
        </h5>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="selectedDate = null">
          Cerrar
        </button>
      </div>

      <div v-if="!selectedDayItems.length" class="text-muted text-center py-4">
        No hay partes este día
      </div>
      <parte-list-card
        v-for="item in selectedDayItems"
        :key="item.albaran.id"
        :item="item"
        @click="$emit('select', item)"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import ParteListCard from './ParteListCard.vue';
import {
  getParteCalendarDate,
  getParteNumero,
  getParteTitulo,
} from '@/util/parteSat';

const MAX_CLIENTE_LEN = 18;

export default {
  name: 'PartesCalendar',
  components: { ParteListCard },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    mes: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      weekdays: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
      calendarDate: moment(this.mes, 'YYYY-MM').toDate(),
      selectedDate: null,
    };
  },
  watch: {
    mes(val) {
      this.calendarDate = moment(val, 'YYYY-MM').toDate();
      this.selectedDate = null;
    },
  },
  computed: {
    monthLabel() {
      return moment(this.calendarDate).format('MMMM YYYY');
    },
    calendarEvents() {
      return (this.items || [])
        .map((item) => {
          const date = getParteCalendarDate(item);
          if (!date) return null;
          const parte = item.parte_sat || {};
          const cliente =
            parte.cliente || item.albaran?.cliente || '';
          const clienteCorto =
            cliente.length > MAX_CLIENTE_LEN
              ? `${cliente.slice(0, MAX_CLIENTE_LEN)}…`
              : cliente;
          return {
            id: `${item.albaran?.id || 'x'}-${parte.idParte || 'np'}`,
            date,
            item,
            numParte: getParteNumero(item),
            titulo: getParteTitulo(item),
            clienteCorto: clienteCorto || '—',
            estado: parte.estado || '—',
          };
        })
        .filter(Boolean);
    },
    calendarDays() {
      const year = this.calendarDate.getFullYear();
      const month = this.calendarDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const firstDayColumn = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
      const days = [];
      const todayStr = moment().format('YYYY-MM-DD');

      for (let i = 0; i < firstDayColumn; i += 1) {
        days.push({ isEmpty: true, events: [] });
      }

      const cursor = new Date(firstDay);
      while (cursor <= lastDay) {
        const dateStr = moment(cursor).format('YYYY-MM-DD');
        const events = this.calendarEvents
          .filter((e) => e.date === dateStr)
          .sort((a, b) => String(a.numParte).localeCompare(String(b.numParte)));
        days.push({
          date: dateStr,
          day: cursor.getDate(),
          isCurrentMonth: true,
          isToday: dateStr === todayStr,
          isEmpty: false,
          events,
        });
        cursor.setDate(cursor.getDate() + 1);
      }

      return days;
    },
    selectedDayItems() {
      if (!this.selectedDate) return [];
      return this.calendarEvents
        .filter((e) => e.date === this.selectedDate)
        .map((e) => e.item);
    },
    selectedDateLabel() {
      if (!this.selectedDate) return '';
      return moment(this.selectedDate).format('DD/MM/YYYY');
    },
  },
  methods: {
    selectDay(day) {
      this.selectedDate =
        this.selectedDate === day.date ? null : day.date;
    },
    prevMonth() {
      this.calendarDate = moment(this.calendarDate).subtract(1, 'month').toDate();
      this.selectedDate = null;
      this.$emit('month-change', moment(this.calendarDate).format('YYYY-MM'));
    },
    nextMonth() {
      this.calendarDate = moment(this.calendarDate).add(1, 'month').toDate();
      this.selectedDate = null;
      this.$emit('month-change', moment(this.calendarDate).format('YYYY-MM'));
    },
  },
};
</script>

<style scoped>
.calendar-grid {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  overflow: hidden;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  background: #f6f9fc;
  border-bottom: 1px solid #dee2e6;
}

.calendar-weekday {
  padding: 0.5rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.8rem;
  color: #525f7f;
  border-right: 1px solid #dee2e6;
}

.calendar-weekday:last-child {
  border-right: none;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.calendar-day {
  border-right: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  min-height: 88px;
  max-height: 88px;
  padding: 0.35rem 0.4rem;
  background: #fff;
  cursor: pointer;
  overflow: hidden;
  transition: background-color 0.15s;
}

.calendar-day:nth-child(7n) {
  border-right: none;
}

.calendar-day:hover:not(.empty-day) {
  background: #f0f3ff;
}

.calendar-day.today {
  background: #e8f4fd;
}

.calendar-day.today .day-number {
  background: #5e72e4;
  color: #fff;
  border-radius: 50%;
  width: 1.6rem;
  height: 1.6rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.calendar-day.has-events {
  background: #fafbff;
}

.calendar-day.selected {
  background: #eef1ff;
  box-shadow: inset 0 0 0 2px #5e72e4;
}

.calendar-day.empty-day {
  background: #f8f9fa;
  cursor: default;
}

.day-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.2rem;
}

.day-number {
  font-weight: 700;
  font-size: 0.85rem;
  color: #32325d;
}

.day-count {
  background: #5e72e4;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.1rem 0.35rem;
  border-radius: 10px;
  min-width: 1.2rem;
  text-align: center;
}

.day-preview {
  font-size: 0.65rem;
  line-height: 1.3;
  color: #525f7f;
}

.preview-line {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-more {
  color: #5e72e4;
  font-weight: 600;
  margin-top: 0.15rem;
}

.day-detail-panel {
  background: #f6f9fc;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
}

.day-detail-header h5 {
  font-size: 1rem;
  color: #32325d;
}
</style>
